import {
  LayersIcon,
  TrendingUpIcon,
  TargetIcon,
  FlameIcon,
  BatteryChargingIcon,
  FlagIcon,
  CalendarDaysIcon,
  ZapIcon,
  ClipboardCheckIcon,
  ActivityIcon,
} from 'lucide-react';

export const phases = [
  {
    n: '01',
    name: 'Le fondamenta',
    short: 'Costruisci basi solide.',
    weeks: 'Settimane 1–8',
    icon: LayersIcon,
    title: 'Le fondamenta',
    lead: 'Costruisci le basi della tua preparazione.',
    desc: "Una preparazione di lunga distanza richiede struttura, costanza e abitudini sostenibili. Questa prima fase ti aiuta a comprendere i fondamenti dell'organizzazione dell'allenamento e a costruire una base da cui partire.",
  },
  {
    n: '02',
    name: 'Lo sviluppo',
    short: 'Sviluppa progressivamente la tua resistenza.',
    weeks: 'Settimane 9–20',
    icon: TrendingUpIcon,
    title: 'Lo sviluppo',
    lead: 'Sviluppa progressivamente la tua resistenza.',
    desc: "Organizza il lavoro su nuoto, ciclismo e corsa mentre aumentano gradualmente le esigenze della preparazione. L'obiettivo è comprendere come le diverse discipline possano inserirsi in un percorso progressivo e coerente.",
  },
  {
    n: '03',
    name: 'Preparazione specifica',
    short: 'Allenati in funzione delle esigenze della lunga distanza.',
    weeks: 'Settimane 21–30',
    icon: TargetIcon,
    title: 'La preparazione specifica',
    lead: 'Avvicina la preparazione alle esigenze della lunga distanza.',
    desc: "In questa fase, l'attenzione si sposta maggiormente sulle richieste della competizione e sull'organizzazione di una preparazione più specifica. Comprendi come adattare il percorso agli obiettivi della gara.",
  },
  {
    n: '04',
    name: 'Il picco',
    short: 'Consolida il lavoro svolto.',
    weeks: 'Settimane 31–35',
    icon: FlameIcon,
    title: 'Il picco della preparazione',
    lead: 'Consolida il lavoro svolto.',
    desc: 'Questa fase mette in relazione i diversi elementi della preparazione, con una maggiore attenzione alla specificità del tuo obiettivo. Organizza il lavoro in modo da avere una visione più chiara del percorso compiuto e di ciò che resta da preparare.',
  },
  {
    n: '05',
    name: 'Avvicinamento',
    short: 'Arriva alla partenza preparato, senza accumulare inutilmente fatica.',
    weeks: 'Settimane 36–40',
    icon: BatteryChargingIcon,
    title: 'La fase di avvicinamento',
    lead: 'Riduci progressivamente il carico e prepara la partenza.',
    desc: "Comprendi il ruolo della fase finale della preparazione e organizza le attività che precedono la competizione. L'obiettivo è arrivare alla linea di partenza con un piano chiaro, evitando decisioni improvvisate negli ultimi giorni.",
  },
  {
    n: '06',
    name: 'Strategia di gara',
    short: 'Trasforma la preparazione in un piano operativo.',
    weeks: 'Giorno di gara',
    icon: FlagIcon,
    title: 'La strategia di gara',
    lead: "L'allenamento ti porta alla partenza. La strategia ti aiuta a organizzare la gara.",
    desc: 'Metti in relazione gestione del ritmo, alimentazione, transizioni, preparazione e decisioni operative in un unico quadro di riferimento.',
  },
];

export const tools = [
  { verb: 'Pianifica', name: 'Il Calendario di Allenamento Triathlon 40 Settimane™', icon: CalendarDaysIcon },
  { verb: 'Alimentati', name: 'La Guida Pratica all’Alimentazione del Triatleta™', icon: ZapIcon },
  { verb: 'Preparati', name: 'La Checklist della Settimana di Gara™', icon: ClipboardCheckIcon },
  { verb: 'Monitora', name: 'Il Diario di Progressione del Triatleta™', icon: ActivityIcon },
];

export const systemIncludes = [
  'Strutture per organizzare il nuoto',
  'Principi per la preparazione in bicicletta',
  'Concetti di progressione nella corsa',
  'Lavoro complementare e forza',
  'Considerazioni sul recupero',
  "Organizzazione dell'allenamento",
  'Monitoraggio dei progressi',
  'Principi di alimentazione',
  "Considerazioni sull'idratazione",
  'Strutture per la preparazione alla gara',
  'Concetti per la gestione della competizione',
  'Checklist pratiche',
];

export type BonusAccent = 'electric' | 'perf' | 'ember' | 'aqua';

export const bonuses: {
  n: string;
  accent: BonusAccent;
  title: string;
  subtitle: string;
  desc: string[];
  tags: string[];
  price: string;
  included: string;
  cover: string;
}[] = [
  {
    n: '01',
    accent: 'electric',
    title: 'Il Calendario di Allenamento Triathlon 40 Settimane™',
    subtitle: 'La tua roadmap visiva dalla preparazione alla gara',
    desc: [
      'Smetti di chiederti continuamente come organizzare il percorso a lungo termine.',
      "Il calendario ti aiuta a visualizzare le principali fasi della preparazione e a mantenere una visione d'insieme del percorso verso la competizione.",
    ],
    tags: [
      'Sett. 1–8 · Fondamenta',
      'Sett. 9–20 · Sviluppo',
      'Sett. 21–30 · Specifica',
      'Sett. 31–35 · Picco',
      'Sett. 36–40 · Avvicinamento',
    ],
    price: '32 €',
    included: 'INCLUSO',
    cover: 'https://cdn.magicpatterns.com/uploads/4NbPB5ixeeHYT8v7J6uaz4/Calendario_de_entrenamiento_de_triatlon.webp',
  },
  {
    n: '02',
    accent: 'perf',
    title: 'La Guida Pratica all’Alimentazione del Triatleta™',
    subtitle: 'Organizza alimentazione e idratazione per gli allenamenti e la gara',
    desc: [
      "L'allenamento richiede energia. Una gara di lunga distanza richiede anche un'attenta pianificazione.",
      "Questa guida ti aiuta a organizzare gli aspetti fondamentali dell'alimentazione e dell'idratazione durante la preparazione.",
    ],
    tags: [
      'Alimentazione quotidiana',
      "Prima dell'allenamento",
      'Idratazione ed elettroliti',
      "Durante l'attività",
      'Nutrizione per il recupero',
      "Alimentazione in gara",
    ],
    price: '29 €',
    included: 'INCLUSA',
    cover: 'https://cdn.magicpatterns.com/uploads/vkV5NpQ6zHPPhSWdFTEq5v/Calendario_de_entrenamiento_de_triatlon_(1).webp',
  },
  {
    n: '03',
    accent: 'ember',
    title: 'La Checklist della Settimana di Gara™',
    subtitle: 'Una checklist pratica per i sette giorni che precedono la partenza',
    desc: [
      "L'ultima settimana non dovrebbe trasformarsi in una serie di decisioni prese all'ultimo momento.",
      'Questa checklist ti aiuta a organizzare le attività e i controlli che precedono la competizione.',
    ],
    tags: [
      'Pianificazione',
      'Viaggio e spostamenti',
      'Attrezzatura',
      'Nuoto',
      'Ciclismo',
      'Corsa',
      'Mattina della gara',
      'Partenza',
    ],
    price: '32 €',
    included: 'INCLUSA',
    cover: 'https://cdn.magicpatterns.com/uploads/twzzhyobWtj5rjHoSqy4Mb/Calendario_de_entrenamiento_de_triatlon_(2).webp',
  },
  {
    n: '04',
    accent: 'aqua',
    title: 'Il Diario di Progressione del Triatleta™',
    subtitle: 'Registra i tuoi allenamenti. Osserva i progressi. Mantieni la costanza.',
    desc: [
      "L'allenamento diventa più facile da analizzare quando puoi osservare ciò che stai facendo nel tempo.",
      'Utilizza il diario per organizzare e monitorare la preparazione nelle tre discipline.',
    ],
    tags: ['Nuoto', 'Ciclismo', 'Corsa', 'Carico di allenamento', 'Recupero', 'Costanza settimanale'],
    price: '27 €',
    included: 'INCLUSO',
    cover: 'https://cdn.magicpatterns.com/uploads/k1STQEpk7hjTNkEWSk9X8d/Calendario_de_entrenamiento_de_triatlon_(3).webp',
  },
];

export const idealFor = [
  'Triatleti che si preparano a una gara di lunga distanza.',
  'Atleti di endurance che desiderano una struttura più chiara.',
  'Persone che vogliono organizzare meglio nuoto, ciclismo e corsa.',
  'Atleti alle prime esperienze che cercano una roadmap comprensibile.',
  'Triatleti esperti che desiderano un processo di preparazione più strutturato.',
];

export const offerIncludes = [
  'Il metodo in 6 fasi per la lunga distanza',
  'Le fondamenta della preparazione',
  'Lo sviluppo della resistenza',
  'La preparazione specifica',
  'Il picco della preparazione',
  'La fase di avvicinamento',
  'La strategia di gara',
  'Strutture per nuoto, ciclismo e corsa',
  'Principi di alimentazione e idratazione',
  'Preparazione alla competizione',
];
